/* global ACCEPTED_ATTACHMENT_FILE_TYPES */

import React, { Fragment, useEffect, useRef, useState } from 'react';

import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';

import Button from 'CommonComponents/Button';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import ModalFooter from 'CommonComponents/ModalFooter';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

import TrashIcon from 'Assets/icons/trash.svg';

const DEFAULT_ASSUMPTION = { description: '', detailed_description: '', verified: false, attachment: null };

const CentralAssumptionGroupSchema = Yup.object().shape({
    title: Yup.string().required($t('This value is required')),
    assumptions: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().required($t('This value is required')),
            detailed_description: Yup.string(),
            verified: Yup.bool(),
            attachment: Yup.mixed(),
        }),
    ),
});

export default function CentralAssumptionGroupUpsertFrom({
    assumptionGroup,
    venueId,
    storeUrl,
    updateUrl,
    deleteAttachmentUrl,
    downloadAttachmentUrl,
    canDeleteAssumption,
    canVerifyAssumption,
    onSuccess,
    onCancel,
}) {
    const confirm = useConfirmationModal();

    const onCentralAssumptionDelete = (arrayHelpers, index) => {
        // If the assumption already has an ID, it means it exists and can only be deleted by an event admin
        if (!canDeleteAssumption && assumptionGroup?.assumptions[index]?.id) {
            return confirm({
                title: $t('Verify Central Assumption'),
                message: $t('Action only possible by Event Admin'),
                actions: [{ label: $t('Close') }],
            });
        }

        arrayHelpers.remove(index);
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                title: assumptionGroup?.title || '',
                assumptions: assumptionGroup?.assumptions
                    ? assumptionGroup.assumptions.map((assumption) => ({
                          id: assumption.id || null,
                          description: assumption.description || '',
                          detailed_description: assumption.detailed_description || '',
                          verified: assumption.verified || false,
                          attachment: null,
                      }))
                    : [],
            }}
            validationSchema={CentralAssumptionGroupSchema}
            onSubmit={(values, actions) => {
                if (assumptionGroup?.id) {
                    if (venueId) {
                        // if the assumption group is being edited from a venue context, we want to keep the assumption group associated to the current venue only, as we don't want the user to edit the venues from this form
                        values.venue_ids = [venueId];
                    }

                    const url = updateUrl.replace('CENTRAL_ASSUMPTION_GROUP_ID', assumptionGroup.id);
                    axios
                        .post(url, values, {
                            params: { _method: 'PUT' }, // This is a workaround to be able to send multipart/form-data as put request
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        .then((res) => {
                            onSuccess();
                            actions.resetForm();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                } else {
                    if (venueId) {
                        // if the assumption group is being created from a venue context, we want to automatically associate the assumption group to the current venue, as we don't want the user to select the venues from this form
                        values.venue_ids = [venueId];
                    }

                    axios
                        .post(storeUrl, values, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        .then((res) => {
                            onSuccess();
                            actions.resetForm();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField label={$t('Heading')} as="textarea" type="text" name="title"></TextareaFormField>
                    </div>

                    <FieldArray name="assumptions">
                        {(arrayHelpers) => (
                            <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                                <div className="tw-flex tw-flex-row tw-justify-between tw-items-center ">
                                    <FormSectionHeading heading={$t('Central Assumptions')}></FormSectionHeading>
                                    <Button small onClick={() => arrayHelpers.push(DEFAULT_ASSUMPTION)}>
                                        {$t('Add an assumption')}
                                    </Button>
                                </div>
                                {values.assumptions?.map((assumption, index) => {
                                    const assumptionErrors = (errors?.assumptions || [])[index];
                                    const assumptionTouched = (touched?.assumptions || [])[index];

                                    return (
                                        <Fragment key={index}>
                                            <AssumptionField
                                                originalValue={assumptionGroup ? assumptionGroup.assumptions[index] : null}
                                                name={`assumptions.${index}`}
                                                value={assumption}
                                                errors={assumptionErrors}
                                                touched={assumptionTouched}
                                                setFieldValue={setFieldValue}
                                                actions={
                                                    <Button
                                                        small
                                                        variant="danger"
                                                        className="narrow"
                                                        onClick={() => onCentralAssumptionDelete(arrayHelpers, index)}
                                                    >
                                                        <TrashIcon className="tw-w-3 tw-h-3" />
                                                    </Button>
                                                }
                                                deleteUrl={deleteAttachmentUrl}
                                                downloadUrl={downloadAttachmentUrl}
                                                canVerifyAssumption={canVerifyAssumption}
                                            />
                                            {index < values.assumptions?.length - 1 && <hr className="tw-my-1" />}
                                        </Fragment>
                                    );
                                })}
                            </div>
                        )}
                    </FieldArray>
                    <ModalFooter
                        cancelButtonText={$t('Cancel')}
                        primaryButtonText={$t('Save')}
                        submitButtonVariant={'primary'}
                        onCancel={onCancel}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                    ></ModalFooter>
                </>
            )}
        </Formik>
    );
}

function AssumptionField({ originalValue, name, value, errors, touched, actions, setFieldValue, deleteUrl, downloadUrl, canVerifyAssumption }) {
    const [originalAttachment, setOriginalAttachment] = useState(null);

    const attachmentInputRef = useRef(null);

    const confirm = useConfirmationModal();

    useEffect(() => {
        if (originalValue && originalValue.attachment) {
            setOriginalAttachment(originalValue.attachment);
        }
    }, [originalValue]);

    useEffect(() => {
        if (!!value.notify_at && !value.set_notification) {
            setFieldValue(`${name}.set_notification`, true);
        }
    }, [value.notify_at, value.set_notification]);

    const onVerifyToggle = (verify) => {
        // Only allow verification/un-verification for event admins
        if (!canVerifyAssumption) {
            return confirm({
                title: $t('Verify Central Assumption'),
                message: $t('Action only possible by Event Admin'),
                actions: [{ label: $t('Close') }],
            });
        }

        setFieldValue(`${name}.verified`, verify);
    };

    return (
        <div className="tw-grid tw-grid-cols-12 tw-gap-4">
            <div className="tw-flex tw-flex-col tw-col-span-12 md:tw-col-span-11 tw-space-y-4">
                <TextareaFormField label={$t('Description')} as="textarea" type="text" name={`${name}.description`}></TextareaFormField>
                <TextareaFormField
                    label={$t('Detailed Description')}
                    as="textarea"
                    type="text"
                    name={`${name}.detailed_description`}
                    style={{ height: 192 }}
                ></TextareaFormField>
                {!originalAttachment ? (
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <input
                            ref={attachmentInputRef}
                            type="file"
                            className={errors?.attachment && touched?.attachment ? 'tw-border tw-border-red-500 file-input' : 'tw-border-0 file-input'}
                            name={`${name}.attachment`}
                            hidden
                            accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                            onChange={(event) => {
                                setFieldValue(`${name}.attachment`, event.currentTarget.files[0]);
                            }}
                        />
                        {errors?.attachment && touched?.attachment && <div className="tw-text-red-500 tw-text-sm">{errors.attachment}</div>}
                    </div>
                ) : (
                    <Attachment attachment={originalAttachment} deleteUrl={deleteUrl} downloadUrl={downloadUrl} onDeleted={() => setOriginalAttachment(null)} />
                )}
                <ToggleFormField
                    label={$t('Verified')}
                    type="checkbox"
                    name={`${name}.verified`}
                    id={`${name}_verified`}
                    onChange={(e) => onVerifyToggle(e.target.checked)}
                ></ToggleFormField>
            </div>
            <div className="tw-col-span-12 md:tw-col-span-1 tw-flex tw-flex-col tw-mt-6 tw-items-center">{actions}</div>
        </div>
    );
}

function Attachment(props) {
    const { attachment, deleteUrl, downloadUrl, onDeleted } = props;

    const confirm = useConfirmationModal();

    const toast = useToast();

    const triggerDeleteAttachment = () => {
        const url = deleteUrl.replace('CENTRAL_ASSUMPTION_ID', attachment.central_assumption_id);

        axios
            .delete(url)
            .then(() => {
                toast.success($t('Attachment deleted'));
                onDeleted && props.onDeleted(attachment);
            })
            .catch(() => toast.error($t('An error occurred, please try again')));
    };

    const deleteAttachment = () => {
        confirm({
            title: $t('Delete attachment'),
            message: $t('Are you sure you want to delete {{name}}?', { name: attachment?.file_name }),
            onConfirm: () => triggerDeleteAttachment(),
        });
    };

    const downloadAttachment = (e) => {
        e.preventDefault();
        const url = downloadUrl.replace('CENTRAL_ASSUMPTION_ID', attachment.central_assumption_id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.file_name);
            document.body.appendChild(link);
            link.click();
        });
    };

    return (
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
            <div key={attachment.id} className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                <a href="#" onClick={(e) => downloadAttachment(e)}>
                    {attachment.file_name}
                </a>
                <Button small variant="danger" onClick={() => deleteAttachment()}>
                    <TrashIcon className="tw-w-3 tw-h-3 tw-mr-2" />
                    {$t('Delete')}
                </Button>
            </div>
        </div>
    );
}
